/**
 * Socket.IO client service for NepWed real-time messaging.
 *
 * Usage:
 *   import { connectSocket, disconnectSocket, onNewMessage, joinInquiry, ... } from '../services/socket';
 *
 * The socket only connects when the realtime_chat_enabled setting is 'true'.
 * When disabled, all functions become no-ops and the 15-second polling
 * continues to work as a fallback.
 *
 * Listeners and joined conversation rooms are kept here rather than on the
 * socket instance. Components subscribe on mount, which is usually before
 * AuthContext has fetched the settings and called connectSocket(); attaching
 * straight to a not-yet-created socket silently dropped those subscriptions.
 * The same registry survives a reconnect or a new session (logout/login,
 * dashboard switch), and rooms are re-joined on every connect because the
 * server forgets them when a socket drops.
 */

import { io } from 'socket.io-client';

// Derive the Socket.IO server URL from the API URL (strip the /api path)
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SOCKET_URL = API_URL.replace(/\/api\/?$/, '') || undefined; // undefined = same origin

// Events the server pushes to clients
const SERVER_EVENTS = ['new_message', 'typing', 'stop_typing', 'unread_update', 'notification_alert', 'rsvp_update'];

let socket = null;
let socketToken = null;
let enabled = false;

const listeners = new Map();      // event name -> Set of callbacks
const joinedInquiries = new Map(); // inquiry id -> number of open views of that thread

function subscribe(event, callback) {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(callback);
  return () => listeners.get(event)?.delete(callback);
}

/**
 * Connect to the Socket.IO server.
 * @param {string} token  – JWT auth token
 * @param {boolean} realtimeEnabled – whether admin has enabled realtime chat
 */
export function connectSocket(token, realtimeEnabled = true) {
  enabled = realtimeEnabled;
  if (!enabled || !token) return;
  // Already connected (or connecting) for this session
  if (socket && socketToken === token) return;
  // A different token means a new session or a switched dashboard — the server
  // reads the role from the token, so the old connection must not linger.
  if (socket) socket.disconnect();

  const s = io(SOCKET_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionAttempts: 10,
  });
  socket = s;
  socketToken = token;

  SERVER_EVENTS.forEach((event) => {
    s.on(event, (payload) => {
      if (socket !== s) return; // superseded by a newer connection
      (listeners.get(event) || []).forEach((cb) => cb(payload));
    });
  });

  s.on('connect', () => {
    console.log('[Socket.IO] Connected:', s.id);
    // Rooms do not survive a reconnect; join every thread still on screen.
    joinedInquiries.forEach((count, inquiryId) => s.emit('join_inquiry', { inquiryId }));
  });

  s.on('connect_error', (err) => {
    console.warn('[Socket.IO] Connection error:', err.message);
  });

  s.on('disconnect', (reason) => {
    console.log('[Socket.IO] Disconnected:', reason);
  });
}

/**
 * Disconnect from the server.
 */
export function disconnectSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  socketToken = null;
  joinedInquiries.clear();
}

/**
 * Join a conversation room to receive messages for a specific inquiry.
 * Safe to call before the socket connects — the join is sent on connect.
 */
export function joinInquiry(inquiryId) {
  if (!inquiryId) return;
  const count = joinedInquiries.get(inquiryId) || 0;
  joinedInquiries.set(inquiryId, count + 1);
  if (count === 0 && socket?.connected && enabled) socket.emit('join_inquiry', { inquiryId });
}

/**
 * Leave a conversation room. The room is only left once no open view of that
 * thread remains (e.g. the floating chat and the vendor page can show the same
 * conversation at once).
 */
export function leaveInquiry(inquiryId) {
  const count = joinedInquiries.get(inquiryId) || 0;
  if (count === 0) return;
  if (count > 1) {
    joinedInquiries.set(inquiryId, count - 1);
    return;
  }
  joinedInquiries.delete(inquiryId);
  if (socket?.connected && enabled) socket.emit('leave_inquiry', { inquiryId });
}

/**
 * Emit a typing indicator.
 */
export function emitTyping(inquiryId) {
  if (!socket?.connected || !enabled) return;
  socket.emit('typing', { inquiryId });
}

/**
 * Emit a stop-typing indicator.
 */
export function emitStopTyping(inquiryId) {
  if (!socket?.connected || !enabled) return;
  socket.emit('stop_typing', { inquiryId });
}

// ───── Event listeners (subscribe/unsubscribe pattern) ─────

/**
 * Listen for new messages. Returns an unsubscribe function.
 */
export function onNewMessage(callback) {
  return subscribe('new_message', callback);
}

/**
 * Listen for typing indicators. Returns an unsubscribe function.
 */
export function onTyping(callback) {
  return subscribe('typing', callback);
}

/**
 * Listen for stop-typing indicators. Returns an unsubscribe function.
 */
export function onStopTyping(callback) {
  return subscribe('stop_typing', callback);
}

/**
 * Listen for unread count updates. Returns an unsubscribe function.
 */
export function onUnreadUpdate(callback) {
  return subscribe('unread_update', callback);
}

/**
 * Listen for notification alerts (rich toast data). Returns an unsubscribe function.
 * Payload: { inquiryId, senderName, senderType, preview, link, messageId, createdAt }
 */
export function onNotificationAlert(callback) {
  return subscribe('notification_alert', callback);
}

/**
 * Listen for RSVP updates. Returns an unsubscribe function.
 * Payload: { weddingId, guestName, rsvpStatus, guestCount }
 */
export function onRsvpUpdate(callback) {
  return subscribe('rsvp_update', callback);
}

/**
 * Check if socket is currently connected and enabled.
 */
export function isSocketConnected() {
  return enabled && socket?.connected;
}

/**
 * Window event fired when this tab marks inquiries read (opening a thread in
 * the floating chat, "Mark all as read" in the bell), so the bell badge and the
 * chat bubble refresh together instead of each waiting for its own poll.
 * `detail.from` names the component that sent it.
 */
export const UNREAD_CHANGED_EVENT = 'nepwed:unread-changed';
